import React from 'react'
import { datalayerHandler } from '../../../../utils/datalayer'
import { Image } from '../../../../utils/image'
import { Link } from '../../../../utils/link'
import { textImageBlockClasses } from '../../../../utils/style'
import { convertReactNodeToString, getClassName, htmlDecode } from '../../../../utils/utils'
import { useInView } from '../../../hooks/useInView'
import { TextImageBlock } from '../blocksState'

export type TextImageBlockStyle = Partial<{
  blockClassName: string
  inviewClassName: string
  innerClassName: string
  contentClassName: string
  headerClassName: string
  labelClassName: string
  titleClassName: string
  textClassName: string
  introClassName: string
  footerClassName: string
  clickableWrapperClassName: string
  clickablePrimaryClassName: string
  clickableSecondaryClassName: string
  clickableLabelClassName: string
  clickableIconClassName: string
  figureClassName: string
  imageClassName: string
  pictureClassName: string
  iconPrimaryId: string
  iconSecondaryId: string
}>

export const TextImageBlockLayout = (props: TextImageBlock) => {
  const { inview, index, image, imageLocation, isLinkType, label, primaryAction, secondaryAction, text, title } = props
  const classes = textImageBlockClasses(imageLocation)
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("TextImageBlockLayout inView:", inview),
    !inview?.triggerOnce
  )

  return (
    <section
      key={`block-${index}`}
      id={`io-${index}`}
      ref={ref}
      className={getClassName(classes, 'blockClassName', 'container') + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')}
    >
      <div className={getClassName(classes, 'innerClassName', 'py-5 row')}>
        <div
          className={getClassName(
            classes,
            'contentClassName',
            `col-md-6 mb-4 order-${imageLocation == 'left' ? 2 : 1}`
          )}
        >
          {(title || label) && (
            <header className={getClassName(classes, 'headerClassName', '')}>
              {label && <span className={getClassName(classes, 'labelClassName', 'd-block small')}>{label}</span>}
              {title && (
                <h2 className={getClassName(classes, 'titleClassName', 'mb-3')}>
                  <span id={`${index}`} aria-hidden="true" className="block__anchor" />
                  {title}
                </h2>
              )}
            </header>
          )}

          {text && (
            <div className={getClassName(classes, 'textClassName', '')}>
              <div className={getClassName(classes, 'introClassName', '')} dangerouslySetInnerHTML={{ __html: text }} />
            </div>
          )}

          {((primaryAction && primaryAction.title) || (secondaryAction && secondaryAction.title)) && (
            <footer className={getClassName(classes, 'footerClassName', '')}>
              {primaryAction && primaryAction.title && (
                <div className={getClassName(classes, 'clickableWrapperClassName', 'd-inline-block')}>
                  <Link
                    url={primaryAction.url}
                    title={primaryAction.title}
                    className={
                      !isLinkType
                        ? getClassName(classes, 'clickablePrimaryClassName', 'btn btn-dark mr-2 d-inline-flex')
                        : getClassName(classes, 'clickableSecondaryClassName', 'btn btn-link px-0 mr-2 d-inline-flex')
                    }
                    additionalOnClick={() =>
                      datalayerHandler('text-image', undefined, undefined, {
                        kind: 'link',
                        title: convertReactNodeToString(title),
                        link: primaryAction.url,
                        label: primaryAction.title,
                      })
                    }
                  >
                    <span className={getClassName(classes, 'clickableLabelClassName', '')}>
                      {htmlDecode(primaryAction.title)}
                    </span>
                    {primaryAction.SVGIcon && (
                      <svg
                        className={getClassName(classes, 'clickableIconClassName', 'ml-3')}
                        aria-hidden="true"
                        width={primaryAction.SVGIcon.width ? primaryAction.SVGIcon.width : '24px'}
                        height={primaryAction.SVGIcon.height ? primaryAction.SVGIcon.height : '24px'}
                      >
                        <use
                          xlinkHref={getClassName(
                            classes,
                            !isLinkType ? 'iconPrimaryId' : 'iconSecondaryId',
                            '#icon--chevron-right-large'
                          )}
                        />
                      </svg>
                    )}
                  </Link>
                </div>
              )}

              {secondaryAction && secondaryAction.title && (
                <div className={getClassName(classes, 'clickableWrapperClassName', 'd-inline-block')}>
                  <Link
                    url={secondaryAction.url}
                    title={secondaryAction.title}
                    className={getClassName(
                      classes,
                      'clickableSecondaryClassName',
                      'btn btn-link px-0 mr-2 d-inline-flex'
                    )}
                    additionalOnClick={() =>
                      datalayerHandler('text-image', undefined, undefined, {
                        kind: 'link',
                        title: convertReactNodeToString(title),
                        link: secondaryAction.url,
                        label: secondaryAction.title,
                      })
                    }
                  >
                    <span className={getClassName(classes, 'clickableLabelClassName', '')}>
                      {htmlDecode(secondaryAction.title)}
                    </span>
                    {secondaryAction.SVGIcon && (
                      <svg
                        className={getClassName(classes, 'clickableIconClassName', 'ml-3')}
                        aria-hidden="true"
                        width={secondaryAction.SVGIcon.width ? secondaryAction.SVGIcon.width : '24px'}
                        height={secondaryAction.SVGIcon.height ? secondaryAction.SVGIcon.height : '24px'}
                      >
                        <use xlinkHref={getClassName(classes, 'iconSecondaryId', '#icon--chevron-right-large')} />
                      </svg>
                    )}
                  </Link>
                </div>
              )}
            </footer>
          )}
        </div>

        {image && (
          <div
            className={getClassName(
              classes,
              'figureClassName',
              `col-md-6 mb-4 order-${imageLocation == 'left' ? 1 : 2}`
            )}
          >
            <Image
              {...image}
              classNames={{
                image: getClassName(classes, 'imageClassName', 'w-100'),
                picture: getClassName(classes, 'pictureClassName', ''),
              }}
            />
          </div>
        )}
      </div>
    </section>
  )
}
